import {isNil} from 'ramda';

import type {useHttpCalls} from '../api/useHttpCalls';
import {VIRTUAL_PRESET_ID} from '../constants/VIRTUAL_PRESET_ID';
import type {Preset} from '../types/Api';
import {migratePresetColor} from './migratePresetColor';
import {
  loadPinnedPresetIds,
  loadPresetColors,
  savePinnedPresetIds,
  savePresetColors,
} from './presetLocalPrefs';

type MigrationHttp = Pick<ReturnType<typeof useHttpCalls>, 'getPreset' | 'updateRenamePreset'>;

/** Move legacy custom preferences to the backend; failed writes remain retryable. */
export async function migratePresetLocalPrefs(
  gridCode: string,
  presets: Preset[],
  http: MigrationHttp
) {
  const colors = loadPresetColors(gridCode);
  const pinnedIds = loadPinnedPresetIds(gridCode);
  if (colors.size === 0 && [...pinnedIds].every((id) => id === VIRTUAL_PRESET_ID)) {
    return presets;
  }

  const migratedColors = new Map<string, string>();
  const migratedPins = new Set<string>();
  const migrated = await Promise.all(
    presets.map(async (preset) => {
      let next = preset;
      const color = colors.get(preset.id);
      if (!isNil(color)) {
        try {
          const withColor = await migratePresetColor(preset, color, http);
          if (!isNil(withColor)) {
            next = withColor;
            migratedColors.set(preset.id, color);
          }
        } catch {
          // Leave the original entry in storage for the next load.
        }
      }
      if (pinnedIds.has(preset.id) && !preset.isPinned) {
        try {
          const current = (await http.getPreset(preset.id, {fresh: true, forCopy: true})) ?? null;
          if (isNil(current)) {
            return next;
          }
          await http.updateRenamePreset(preset.id, {
            title: current.title,
            description: current.description,
            isPinned: true,
          });
          next = {...next, isPinned: true};
        } catch {
          // Leave the original entry in storage for the next load.
        }
      }
      if (pinnedIds.has(preset.id) && next.isPinned) {
        migratedPins.add(preset.id);
      }
      return next;
    })
  );

  // Only remove successfully migrated snapshots; other instances share this storage key.
  const currentPins = loadPinnedPresetIds(gridCode);
  migratedPins.forEach((id) => currentPins.delete(id));
  savePinnedPresetIds(gridCode, currentPins);
  const currentColors = loadPresetColors(gridCode);
  migratedColors.forEach((color, id) => {
    if (currentColors.get(id) === color) {
      currentColors.delete(id);
    }
  });
  savePresetColors(gridCode, currentColors);
  return migrated;
}
